import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Tabs,
  Steps,
  Button,
  Select,
  Table,
  Progress,
  Upload,
  message,
  Typography,
  Space,
  Alert,
  Avatar,
  Checkbox,
  Input,
} from 'antd';
import { DownloadOutlined, UploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';
import { hasRole } from '../utils/permissionUtils';

const { Title, Text } = Typography;

const STUDIO_BUNDLE_EXT = '.studio-bundle.zip';

/** 匯出 ZIP 下載檔名：流程名稱 + 下載當下日期時間（本地時區） */
function buildStudioBundleDownloadFileName(workflowName) {
  const raw = String(workflowName ?? '').trim() || 'workflow';
  const safe = raw
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/[\u0000-\u001f]/g, '')
    .trim()
    .slice(0, 120);
  const base = safe || 'workflow';
  return `${base}_${dayjs().format('YYYYMMDD_HHmmss')}${STUDIO_BUNDLE_EXT}`;
}

function useWorkflowOptions() {
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/workflowdefinitions?page=1&pageSize=500&sortBy=updatedAt&sortOrder=desc', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'load failed');
      setWorkflows(data.data || []);
    } catch (e) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return { loading, workflows, reload: load };
}

export default function WorkflowPackagePage() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'import' ? 'import' : 'export';

  const { loading: wfLoading, workflows, reload: reloadWf } = useWorkflowOptions();
  const [userInfo, setUserInfo] = useState({});
  useEffect(() => {
    try {
      setUserInfo(JSON.parse(localStorage.getItem('userInfo') || '{}'));
    } catch {
      setUserInfo({});
    }
  }, []);
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const [wfId, setWfId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportStep, setExportStep] = useState(0);
  const [excluded, setExcluded] = useState({});
  const [exportJobId, setExportJobId] = useState(null);
  const [exportPoll, setExportPoll] = useState(null);

  const [importStep, setImportStep] = useState(0);
  const [previewKey, setPreviewKey] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  /** 匯入預覽「匯入為」欄位：`{kind}:{exportId}` → 名稱 */
  const [importAsByKey, setImportAsByKey] = useState({});
  const [importJobId, setImportJobId] = useState(null);
  const [importPoll, setImportPoll] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const importPreviewRows = useMemo(() => {
    if (!importPreview) return [];
    const items = importPreview.items;
    if (items?.length > 0) return items;
    return importPreview.renames || [];
  }, [importPreview]);

  useEffect(() => {
    if (!importPreview) {
      setImportAsByKey({});
      return;
    }
    const rows = importPreview.items?.length > 0 ? importPreview.items : importPreview.renames || [];
    const next = {};
    rows.forEach((it) => {
      const key = `${it.kind}:${it.exportId}`;
      next[key] = it.suggestedName ?? it.originalName ?? '';
    });
    setImportAsByKey(next);
  }, [importPreview]);

  const formatWfUpdated = useCallback(
    (iso) => {
      if (!iso) return '—';
      try {
        return dayjs(iso).format('YYYY-MM-DD HH:mm');
      } catch {
        return String(iso);
      }
    },
    []
  );

  const wfSelectOptions = useMemo(() => {
    return (workflows || []).map((w) => {
      const id = w.id ?? w.Id;
      const name = w.name ?? w.Name ?? `#${id}`;
      const companyName = w.companyName ?? w.CompanyName;
      const status = w.status ?? w.Status;
      const updatedAt = w.updatedAt ?? w.UpdatedAt;
      const searchText = [name, companyName, status, updatedAt].filter(Boolean).join(' ').toLowerCase();
      const label = (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isTenantAdmin ? 'minmax(140px,1.2fr) minmax(100px,0.9fr) 88px 128px' : 'minmax(160px,1.4fr) 88px 128px',
            gap: 10,
            alignItems: 'center',
            padding: '4px 0',
            fontSize: 13,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
            {name}
          </span>
          {isTenantAdmin && (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(0,0,0,0.65)' }} title={companyName || ''}>
              {companyName || '—'}
            </span>
          )}
          <span style={{ color: 'rgba(0,0,0,0.65)', whiteSpace: 'nowrap' }} title={status || ''}>
            {status || '—'}
          </span>
          <span style={{ color: 'rgba(0,0,0,0.45)', whiteSpace: 'nowrap', fontSize: 12 }}>{formatWfUpdated(updatedAt)}</span>
        </div>
      );
      return { value: id, label, searchText };
    });
  }, [workflows, isTenantAdmin, formatWfUpdated]);

  const goExportPrev = () => {
    if (exportStep === 1) {
      setExportStep(0);
      setPreview(null);
      setExcluded({});
    }
  };

  const goImportPrev = () => {
    if (importStep === 1) {
      setImportStep(0);
      setPreviewKey(null);
      setImportPreview(null);
    } else if (importStep === 2) {
      setImportStep(1);
      setImportJobId(null);
      setImportPoll(null);
    } else if (importStep === 3) {
      if (importPoll?.failed) {
        setImportStep(1);
        setImportJobId(null);
        setImportPoll(null);
      } else {
        setImportStep(0);
        setPreviewKey(null);
        setImportPreview(null);
        setImportJobId(null);
        setImportPoll(null);
      }
    }
  };

  const resetExport = () => {
    setWfId(null);
    setPreview(null);
    setExcluded({});
    setExportStep(0);
    setExportJobId(null);
    setExportPoll(null);
  };

  const resetImport = () => {
    setImportStep(0);
    setPreviewKey(null);
    setImportPreview(null);
    setImportAsByKey({});
    setImportJobId(null);
    setImportPoll(null);
  };

  const onTabChange = (key) => {
    setSearchParams(key === 'import' ? { tab: 'import' } : { tab: 'export' });
  };

  const runPreviewExport = async () => {
    if (!wfId) {
      message.warning(t('workflowPackage.selectWorkflowFirst'));
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await fetch('/api/studio-bundles/preview-export', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ workflowDefinitionId: wfId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'preview failed');
      setPreview(data);
      setExcluded({});
      setExportStep(1);
      message.success(t('workflowPackage.previewLoaded'));
    } catch (e) {
      message.error(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleExclude = useCallback((kind, id) => {
    const k = `${kind}:${id}`;
    setExcluded((prev) => ({ ...prev, [k]: !prev[k] }));
  }, []);

  const buildExcludePayload = () => {
    const excludeFormIds = [];
    const excludeDataSetIds = [];
    const excludeTemplateIds = [];
    const excludeMetaTemplateKeys = [];
    const excludeAiStaffIds = [];
    const excludeKnowledgeDocumentIds = [];
    (preview?.items || []).forEach((it) => {
      const k = `${it.kind}:${it.id}`;
      if (!excluded[k]) return;
      if (it.kind === 'eform') excludeFormIds.push(it.id);
      else if (it.kind === 'dataset') excludeDataSetIds.push(it.id);
      else if (it.kind === 'template') excludeTemplateIds.push(it.id);
      else if (it.kind === 'metaTemplate') excludeMetaTemplateKeys.push(it.id);
      else if (it.kind === 'aiStaff') excludeAiStaffIds.push(it.id);
      else if (it.kind === 'knowledgeDoc') excludeKnowledgeDocumentIds.push(it.id);
    });
    return {
      excludeFormIds,
      excludeDataSetIds,
      excludeTemplateIds,
      excludeMetaTemplateKeys,
      excludeAiStaffIds,
      excludeKnowledgeDocumentIds,
    };
  };

  const startExport = async () => {
    if (!wfId) return;
    setExportStep(2);
    try {
      const body = {
        workflowDefinitionId: wfId,
        ...buildExcludePayload(),
      };
      const res = await fetch('/api/studio-bundles/export/start', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'export start failed');
      setExportJobId(data.jobId);
    } catch (e) {
      message.error(e.message);
      setExportStep(1);
    }
  };

  useEffect(() => {
    if (!exportJobId) return;
    const tmr = setInterval(async () => {
      try {
        const res = await fetch(`/api/studio-bundles/export/${exportJobId}/status`, { headers: { Authorization: `Bearer ${token}` } });
        const st = await res.json();
        setExportPoll(st);
        if (st.completed) {
          clearInterval(tmr);
          setExportStep(3);
          if (st.failed) message.error(st.error || 'export failed');
        }
      } catch {
        clearInterval(tmr);
      }
    }, 600);
    return () => clearInterval(tmr);
  }, [exportJobId, token]);

  const downloadWithFetch = async () => {
    if (!exportJobId) return;
    try {
      const res = await fetch(`/api/studio-bundles/export/${exportJobId}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildStudioBundleDownloadFileName(preview?.workflowName);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e.message);
    }
  };

  const uploadImport = {
    maxCount: 1,
    beforeUpload: () => false,
    onChange: async ({ file }) => {
      const raw = file.originFileObj || file;
      if (!raw) return;
      const fd = new FormData();
      fd.append('file', raw);
      setImportStep(1);
      try {
        const res = await fetch('/api/studio-bundles/import/preview', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'preview failed');
        setPreviewKey(data.previewKey);
        setImportPreview(data.preview);
        message.success(t('workflowPackage.importPreviewOk'));
      } catch (e) {
        message.error(e.message);
        setImportStep(0);
      }
    },
  };

  const runImport = async () => {
    if (!previewKey) return;
    setImportStep(2);
    try {
      const res = await fetch('/api/studio-bundles/import/execute', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ previewKey, metaTemplateMap: {}, importAsNames: importAsByKey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'import failed');
      setImportJobId(data.jobId);
    } catch (e) {
      message.error(e.message);
      setImportStep(1);
    }
  };

  useEffect(() => {
    if (!importJobId) return;
    const tmr = setInterval(async () => {
      try {
        const res = await fetch(`/api/studio-bundles/import/${importJobId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const st = await res.json();
        setImportPoll(st);
        if (st.completed) {
          clearInterval(tmr);
          setImportStep(3);
          if (!st.failed) message.success(t('workflowPackage.importDone'));
          else message.error(st.error || 'import failed');
        }
      } catch {
        clearInterval(tmr);
      }
    }, 700);
    return () => clearInterval(tmr);
  }, [importJobId, token, t]);

  const exportDepKindLabel = useCallback(
    (kind) => {
      const map = {
        eform: 'workflowPackage.kindEform',
        dataset: 'workflowPackage.kindDataset',
        template: 'workflowPackage.kindTemplate',
        metaTemplate: 'workflowPackage.kindMetaTemplate',
        aiStaff: 'workflowPackage.kindAiStaff',
        knowledgeDoc: 'workflowPackage.kindKnowledgeDoc',
        workflow: 'workflowPackage.kindWorkflow',
      };
      const key = map[kind];
      return key ? t(key) : kind;
    },
    [t]
  );

  const columns = useMemo(
    () => [
      {
        title: t('workflowPackage.colKind'),
        dataIndex: 'kind',
        key: 'kind',
        width: 120,
        render: (kind) => exportDepKindLabel(kind),
      },
      {
        title: t('workflowPackage.colName'),
        dataIndex: 'name',
        key: 'name',
        render: (_, row) =>
          row.kind === 'aiStaff' ? (
            <Space size="small">
              <Avatar src={row.avatarUrl} size={32}>
                {(row.name || '?').charAt(0)}
              </Avatar>
              <span>{row.name}</span>
            </Space>
          ) : (
            row.name
          ),
      },
      {
        title: t('workflowPackage.colInclude'),
        key: 'inc',
        width: 200,
        render: (_, row) =>
          row.kind === 'aiStaff' ? (
            <Checkbox checked={!excluded[`${row.kind}:${row.id}`]} onChange={() => toggleExclude(row.kind, row.id)}>
              {t('workflowPackage.exportAiStaff')}
            </Checkbox>
          ) : (
            <Button size="small" type={excluded[`${row.kind}:${row.id}`] ? 'default' : 'primary'} onClick={() => toggleExclude(row.kind, row.id)}>
              {excluded[`${row.kind}:${row.id}`] ? t('workflowPackage.excluded') : t('workflowPackage.included')}
            </Button>
          ),
      },
    ],
    [t, excluded, toggleExclude, exportDepKindLabel]
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <div style={{ textAlign: 'right', maxWidth: 560 }}>
          <Title level={3} style={{ margin: 0, textAlign: 'right' }}>
            {t('workflowPackage.title')}
          </Title>
          <Text type="secondary" style={{ textAlign: 'right', display: 'block', marginTop: 6, lineHeight: 1.55 }}>
            {t('workflowPackage.cuiDefinition')}
          </Text>
          <Text type="secondary" style={{ textAlign: 'right', display: 'block', marginTop: 4, lineHeight: 1.55 }}>
            {t('workflowPackage.subtitle')}
          </Text>
        </div>
      </div>

      <Tabs
        activeKey={tab}
        onChange={onTabChange}
        style={{ marginTop: 8 }}
        items={[
          {
            key: 'export',
            label: t('workflowPackage.tabExport'),
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Steps
                  current={exportStep}
                  items={[
                    { title: t('workflowPackage.stepSelectWorkflow') },
                    { title: t('workflowPackage.stepReviewDeps') },
                    { title: t('workflowPackage.stepBuilding') },
                    { title: t('workflowPackage.stepDownload') },
                  ]}
                />
                {exportStep === 0 && (
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isTenantAdmin ? 'minmax(140px,1.2fr) minmax(100px,0.9fr) 88px 128px' : 'minmax(160px,1.4fr) 88px 128px',
                        gap: 10,
                        padding: '0 12px',
                        fontSize: 12,
                        color: 'rgba(0,0,0,0.45)',
                        maxWidth: isTenantAdmin ? 640 : 520,
                      }}
                    >
                      <span>{t('workflowPackage.dropdownColName')}</span>
                      {isTenantAdmin && <span>{t('workflowPackage.dropdownColCompany')}</span>}
                      <span>{t('workflowPackage.dropdownColStatus')}</span>
                      <span>{t('workflowPackage.dropdownColUpdated')}</span>
                    </div>
                    <Space wrap>
                      <Select
                        showSearch
                        placeholder={t('workflowPackage.selectWorkflow')}
                        style={{ minWidth: isTenantAdmin ? 640 : 520 }}
                        loading={wfLoading}
                        options={wfSelectOptions}
                        value={wfId}
                        onChange={setWfId}
                        optionLabelProp="label"
                        popupMatchSelectWidth={false}
                        dropdownStyle={{ minWidth: isTenantAdmin ? 640 : 520 }}
                        filterOption={(input, option) => {
                          const o = wfSelectOptions.find((x) => x.value === option?.value);
                          const q = (input || '').trim().toLowerCase();
                          return (o?.searchText || '').includes(q);
                        }}
                      />
                      <Button icon={<ReloadOutlined />} onClick={reloadWf} />
                      <Button type="primary" loading={previewLoading} onClick={runPreviewExport}>
                        {t('workflowPackage.next')}
                      </Button>
                    </Space>
                  </Space>
                )}
                {exportStep >= 1 && preview && (
                  <>
                    <Alert type="info" showIcon message={preview.workflowName} />
                    <Table rowKey={(r) => `${r.kind}:${r.id}`} columns={columns} dataSource={preview.items} pagination={false} size="small" />
                    {exportStep === 1 && (
                      <Space wrap>
                        <Button onClick={goExportPrev}>{t('workflowPackage.prev')}</Button>
                        <Button type="primary" onClick={startExport}>
                          {t('workflowPackage.startExportZip')}
                        </Button>
                      </Space>
                    )}
                  </>
                )}
                {exportStep >= 2 && (
                  <div>
                    <Progress percent={exportPoll?.progressPercent ?? 0} status={exportPoll?.failed ? 'exception' : 'active'} />
                    <Text>{exportPoll?.message || exportPoll?.phase || ''}</Text>
                    {exportStep === 3 && exportPoll?.failed && (
                      <Space wrap style={{ marginTop: 12 }}>
                        <Button onClick={resetExport}>
                          {t('workflowPackage.exportAgain')}
                        </Button>
                      </Space>
                    )}
                  </div>
                )}
                {exportStep === 3 && !exportPoll?.failed && (
                  <>
                    <Space wrap>
                      <Button type="primary" icon={<DownloadOutlined />} onClick={downloadWithFetch}>
                        {t('workflowPackage.downloadZip')}
                      </Button>
                      <Button onClick={resetExport}>
                        {t('workflowPackage.exportAgain')}
                      </Button>
                      <Button onClick={() => { resetImport(); onTabChange('import'); }}>
                        {t('workflowPackage.goImport')}
                      </Button>
                    </Space>
                    {exportPoll?.metaWarnings?.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('workflowPackage.exportMetaWarningsTitle') || `${exportPoll.metaWarnings.length} 個 Meta 模板無法完整匯出`}
                        description={
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {exportPoll.metaWarnings.map((w, i) => (
                              <li key={i} style={{ fontSize: 12, marginBottom: 4 }}>{w}</li>
                            ))}
                          </ul>
                        }
                      />
                    )}
                  </>
                )}
              </Space>
            ),
          },
          {
            key: 'import',
            label: t('workflowPackage.tabImport'),
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Steps
                  current={importStep}
                  items={[
                    { title: t('workflowPackage.stepUpload') },
                    { title: t('workflowPackage.stepPreview') },
                    { title: t('workflowPackage.stepImporting') },
                    { title: t('workflowPackage.stepDone') },
                  ]}
                />
                {importStep === 0 && (
                  <Upload {...uploadImport}>
                    <Button icon={<UploadOutlined />}>{t('workflowPackage.selectZip')}</Button>
                  </Upload>
                )}
                {importStep >= 1 && importPreview && (
                  <>
                    {importPreview.pendingMetaItems?.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('workflowPackage.metaNotice')}
                        description={importPreview.pendingMetaItems.join(', ')}
                      />
                    )}
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={importPreviewRows}
                      rowKey={(r) => `${r.kind}-${r.exportId}`}
                      columns={[
                        {
                          title: t('workflowPackage.colKind'),
                          dataIndex: 'kind',
                          render: (kind) => exportDepKindLabel(kind),
                        },
                        { title: t('workflowPackage.renameFrom'), dataIndex: 'originalName' },
                        {
                          title: t('workflowPackage.renameTo'),
                          key: 'importAs',
                          render: (_, row) => {
                            const mapKey = `${row.kind}:${row.exportId}`;
                            return (
                              <Input
                                value={importAsByKey[mapKey] ?? ''}
                                onChange={(e) =>
                                  setImportAsByKey((prev) => ({ ...prev, [mapKey]: e.target.value }))
                                }
                                placeholder={row.suggestedName ?? row.originalName}
                              />
                            );
                          },
                        },
                      ]}
                    />
                    {importStep === 1 && (
                      <Space wrap>
                        <Button onClick={goImportPrev}>{t('workflowPackage.prev')}</Button>
                        <Button type="primary" onClick={runImport} disabled={!previewKey}>
                          {t('workflowPackage.confirmImport')}
                        </Button>
                      </Space>
                    )}
                  </>
                )}
                {importStep >= 2 && (
                  <div>
                    <Progress percent={importPoll?.progressPercent ?? 0} status={importPoll?.failed ? 'exception' : 'active'} />
                    <Text>{importPoll?.message || importPoll?.phase || ''}</Text>
                    {importStep === 3 && importPoll?.failed ? (
                      <Space wrap style={{ marginTop: 12 }}>
                        <Button onClick={resetImport}>
                          {t('workflowPackage.importAgain')}
                        </Button>
                      </Space>
                    ) : importStep < 3 && (
                      <Space wrap style={{ marginTop: 12 }}>
                        <Button onClick={goImportPrev}>{t('workflowPackage.prev')}</Button>
                      </Space>
                    )}
                  </div>
                )}
                {importStep === 3 && importPoll?.resultPayload && (
                  <>
                    <Alert type="success" message={t('workflowPackage.importDone')} />
                    <Space wrap>
                      <Button onClick={resetImport}>
                        {t('workflowPackage.importAgain')}
                      </Button>
                      <Button onClick={() => { resetExport(); onTabChange('export'); }}>
                        {t('workflowPackage.goExport')}
                      </Button>
                    </Space>
                    {importPoll.metaWarnings?.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('workflowPackage.metaWarningsTitle') || `${importPoll.metaWarnings.length} 個 Meta 模板需注意`}
                        description={
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {importPoll.metaWarnings.map((w, i) => (
                              <li key={i} style={{ fontSize: 12, marginBottom: 4 }}>{w}</li>
                            ))}
                          </ul>
                        }
                      />
                    )}
                  </>
                )}
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
}
